/** A chart block: its kind, an optional title, its width (a share of the text column, as for pictures) and height, the names of its series (one per number of a row) and the labelled rows it draws. */
/** The heights (in pixels) a chart can be given; the public page only accepts these. */
export const CHART_HEIGHTS = [200, 288, 400, 520] as const;

export type ChartData = { chartType: "bar" | "line" | "pie"; title: string; width?: string; height?: number; series: string[]; colors: string[]; rows: { label: string; values: number[] }[] };

/** How many number columns (series) a chart has: the named series, or more if some row carries more numbers. */
export function chartColumns(chart: ChartData): number {
  return Math.max(chart.series.length, ...chart.rows.map((r) => r.values.length), 1);
}

/** The colours with the one at `index` set (a hex, or "" for the theme's own); pies colour each slice, bars and lines each series. */
export function setChartColour(chart: ChartData, index: number, hex: string): string[] {
  return Object.assign(Array.from({ length: Math.max(chart.colors.length, index + 1) }, (_, i) => chart.colors[i] ?? ""), { [index]: hex });
}

/** The rows with one more empty line (label empty, zeros). */
export function addChartRow(chart: ChartData): ChartData["rows"] {
  return [...chart.rows, { label: "", values: Array.from({ length: chartColumns(chart) }, () => 0) }];
}

/** The change that adds one more series (a new column of zeros). */
export function addChartSeries(chart: ChartData): Pick<ChartData, "series" | "rows"> {
  const count = chartColumns(chart);
  return {
    series: [...chart.series, ...Array.from({ length: count - chart.series.length }, () => ""), ""],
    rows: chart.rows.map((r) => ({ label: r.label, values: [...r.values, ...Array.from({ length: count - r.values.length }, () => 0), 0] })),
  };
}

/** The change that removes the series at `index` with its numbers; the last series cannot be removed. */
export function removeChartSeries(chart: ChartData, index: number): Pick<ChartData, "series" | "rows"> {
  if (chart.series.length <= 1) return { series: chart.series, rows: chart.rows };
  return { series: chart.series.filter((_, i) => i !== index), rows: chart.rows.map((r) => ({ label: r.label, values: r.values.filter((_, i) => i !== index) })) };
}

/** The rows with one number changed: line `row`, series `column`. Anything that is not a number counts as 0. */
export function setChartCell(chart: ChartData, row: number, column: number, text: string): ChartData["rows"] {
  const value = Number(text);
  return chart.rows.map((r, i) => (i === row ? { ...r, values: Object.assign([...r.values], { [column]: Number.isFinite(value) ? value : 0 }) } : r));
}

type OldRow = { label: string; values?: number[]; value?: number };

/** Reads a saved chart block, including the older ones that had a single `value` per row and no series names. */
export function normalizeChart(node: Omit<ChartData, "rows" | "series" | "colors"> & { series?: string[]; colors?: string[]; rows?: OldRow[] }): ChartData {
  return { ...node, series: node.series ?? [], colors: node.colors ?? [], rows: (node.rows ?? []).map((row) => ({ label: row.label, values: row.values ?? [row.value ?? 0] })) };
}
